import {
  type RiskGapBackwardsCompatibleStatus,
  riskGapDefaultSubstatuses,
  riskGapUserStatusOptions,
} from '@ctw/shared/api/fhir/models/risk-gap';
import { Button } from '@ctw/shared/components/button';
import { Card } from '@ctw/shared/components/containers/card';
import { TagCheckboxGroup } from '@ctw/shared/components/inputs/tag-checkbox-group';
import { TagRadioGroup } from '@ctw/shared/components/inputs/tag-radio-group';
import { useBuilderConfig } from '@ctw/shared/context/builder-config-context';
import { useTelemetry } from '@ctw/shared/context/telemetry/telemetry-boundary';
import { tw, twx } from '@ctw/shared/utils/tailwind';
import type { RiskGapStatus } from '@zeus-health/types';
import { isEmpty, isEqual } from 'lodash-es';
import { useCallback, useMemo, useState } from 'react';

interface RiskGapStatusFormData {
  status: RiskGapBackwardsCompatibleStatus;
  substatuses: Array<string>;
  notes: string;
}

export interface RiskGapStatusFormProps {
  initialStatus: RiskGapBackwardsCompatibleStatus;
  initialSubstatuses: Array<string>;
  initialNotes: string;
  notesPlaceholder?: string;
  onSubmit: (data: RiskGapStatusFormData) => Promise<void>;
}

export const RiskGapStatusForm = ({
  initialStatus,
  initialSubstatuses,
  initialNotes,
  notesPlaceholder = 'Are there any additional details you would like to include?',
  onSubmit,
}: RiskGapStatusFormProps) => {
  const [selectedStatus, setSelectedStatus] =
    useState<RiskGapBackwardsCompatibleStatus>(initialStatus);
  const [selectedSubstatuses, setSelectedSubstatuses] = useState<Array<string>>(initialSubstatuses);
  const [notes, setNotes] = useState<string>(initialNotes);
  const [loading, setLoading] = useState(false);
  const builderConfig = useBuilderConfig();

  const telemetry = useTelemetry();

  const substatusOptions = useMemo(
    () =>
      builderConfig.riskGaps?.statusOptions?.[selectedStatus as RiskGapStatus]?.substatusOptions ??
      riskGapDefaultSubstatuses[selectedStatus] ??
      [],
    [builderConfig, selectedStatus],
  );

  const isDirty = useMemo(
    () =>
      !(
        isEqual(initialStatus, selectedStatus) &&
        isEqual(initialSubstatuses, selectedSubstatuses) &&
        isEqual(initialNotes, notes)
      ),
    [initialStatus, initialSubstatuses, initialNotes, selectedStatus, selectedSubstatuses, notes],
  );

  const resetForm = useCallback(() => {
    setSelectedStatus(initialStatus);
    setSelectedSubstatuses(initialSubstatuses);
    setNotes(initialNotes);
    setLoading(false);
  }, [initialStatus, initialSubstatuses, initialNotes]);

  return (
    <Card>
      <div className={tw`flex h-full w-full flex-col gap-4 p-2 transition-all duration-100`}>
        <TagRadioGroup
          legend="Status"
          name="risk-gap-status"
          onOptionSelected={(option) => {
            if (option.value !== selectedStatus) {
              setSelectedStatus(option.value as never);
              setSelectedSubstatuses([]);
            }
          }}
          options={riskGapUserStatusOptions}
          selectedOption={selectedStatus}
        />
        <TagCheckboxGroup
          className={twx('transition-all duration-150', {
            'h-0 opacity-0': isEmpty(riskGapDefaultSubstatuses[selectedStatus]),
            'h-fit opacity-100': !isEmpty(riskGapDefaultSubstatuses[selectedStatus]),
          })}
          legend="Details (select any that apply)"
          name="risk-gap-substatus"
          onOptionSelected={(option) => {
            if (!selectedSubstatuses.includes(option.value as never)) {
              setSelectedSubstatuses((selected) => [...selected, option.value as never]);
            }
          }}
          onOptionDeselected={(option) => {
            if (selectedSubstatuses.includes(option.value as never)) {
              setSelectedSubstatuses((selected) =>
                selected.filter((substatus) => substatus !== option.value),
              );
            }
          }}
          options={substatusOptions}
          selectedOptions={selectedSubstatuses}
        />
        <label>
          <div
            className={tw`mb-1 block font-medium text-content-subtle text-xs uppercase tracking-wide`}
          >
            Notes
          </div>
          <textarea
            placeholder={notesPlaceholder}
            onChange={(event) => setNotes(event.currentTarget.value)}
            value={notes}
            rows={4}
            className={tw`w-full resize-none`}
          />
          <div className={tw`mt-2 flex w-full gap-3`}>
            <Button
              type="button"
              size="xs"
              variant="primary"
              className={tw`min-w-[75px]`}
              disabled={loading}
              onClick={() => {
                telemetry.trackInteraction('risk_gap_status_change_form_submit', {
                  riskGapNewStatus: selectedStatus,
                  riskGapNewSubstatuses: selectedSubstatuses,
                });
                setLoading(true);
                void onSubmit({
                  status: selectedStatus as RiskGapBackwardsCompatibleStatus,
                  substatuses: selectedSubstatuses,
                  notes,
                }).finally(() => {
                  setLoading(false);
                });
              }}
            >
              Save
            </Button>
            <Button
              type="button"
              size="xs"
              variant="secondary"
              disabled={loading || !isDirty}
              onClick={resetForm}
              className={tw`min-w-[75px]`}
            >
              Reset
            </Button>
          </div>
        </label>
      </div>
    </Card>
  );
};
